import { ReactNode } from "react";
import {
  Badge,
  BadgeYellow,
  BadgeLime,
  BadgeIndigo,
  BadgeZinc,
  BadgeAmber,
} from "../badge/Badge";
import { Icon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { TooltipContainer } from "@sellar-io/components";

export type IndicatorTypeType =
  | "awaitingTerms"
  | "credit"
  | "default"
  | "paymentOnDelivery"
  | "proForma"
  | "requestPricing"
  | "sellarCredit"
  | "termsRequested";

export const usePaymentTermsIndicatorDirect = ({
  indicatorType,
  overrideText,
  tooltipContent,
}: {
  indicatorType?: IndicatorTypeType;
  overrideText?: ReactNode;
  tooltipContent?: ReactNode;
}): {
  backgroundColor: string | null;
  mainPaymentOption: string | null;
  type: IndicatorTypeType | null;
  textColor: string | null;
  borderColor: string | null;
  compact: ReactNode | null;
  full: ReactNode | null;
  fullTextOnly: ReactNode | null;
  supplierCard: ReactNode | null;
} => {
  const getIndicatorOptions = (): {
    type: IndicatorTypeType | null;
    mainPaymentOption: string | null;
    longText: string | null;
    backgroundColor: string | null;
    textColor: string | null;
    borderColor: string | null;
    icon: IconType | null;
    supplierCardIcon: IconType | null;
    badge: null | ((content: ReactNode) => ReactNode);
  } => {
    switch (indicatorType) {
      case "awaitingTerms":
        return {
          type: "awaitingTerms",
          mainPaymentOption: "Awaiting terms",
          longText: "",
          backgroundColor: "bg-indigo-100",
          textColor: "text-indigo-700",
          borderColor: "border-indigo-300",
          icon: "INFORMATION" as IconType,
          supplierCardIcon: "" as IconType,
          badge: (content: ReactNode) => (
            <BadgeIndigo icon="INFORMATION">{content}</BadgeIndigo>
          ),
        };

      case "credit":
        return {
          type: "credit",
          mainPaymentOption: "Credit",
          longText: "Order with Credit",
          backgroundColor: "bg-lime-100",
          textColor: "text-lime-700",
          borderColor: "border-lime-300",
          icon: "LIGHTNING_BOLT" as IconType,
          supplierCardIcon: "CALENDAR_CLOCK_OUTLINE" as IconType,
          badge: (content: ReactNode) => (
            <BadgeLime icon="CALENDAR_CLOCK_OUTLINE">{content}</BadgeLime>
          ),
        };

      case "default":
        return {
          type: "default",
          mainPaymentOption: "Default (pro-forma)",
          longText: "Order to get terms assigned",
          backgroundColor: "bg-zinc-50",
          textColor: null,
          borderColor: null,
          icon: "LIGHTNING_BOLT" as IconType,
          supplierCardIcon: "PROGRESS_HELPER" as IconType,
          badge: (content: ReactNode) => (
            <BadgeZinc icon="PROGRESS_HELPER">{content}</BadgeZinc>
          ),
        };

      case "paymentOnDelivery":
        return {
          type: "paymentOnDelivery",
          mainPaymentOption: "Pay on delivery",
          longText: "Order now and pay on delivery",
          backgroundColor: "bg-amber-100",
          textColor: "text-amber-700",
          borderColor: "border-amber-300",
          icon: "LIGHTNING_BOLT" as IconType,
          supplierCardIcon: "CREDIT_CARD_MARKER_OUTLINE" as IconType,
          badge: (content: ReactNode) => (
            <BadgeAmber icon="CREDIT_CARD_MARKER_OUTLINE">{content}</BadgeAmber>
          ),
        };

      case "proForma":
        return {
          type: "proForma",
          mainPaymentOption: "Pro-forma",
          longText: "Place a pro-forma order",
          backgroundColor: "bg-yellow-100",
          textColor: "text-yellow-700",
          borderColor: "border-yellow-300",
          icon: "LIGHTNING_BOLT" as IconType,
          supplierCardIcon: "RECEIPT_TEXT_OUTLINE" as IconType,
          badge: (content: ReactNode) => (
            <BadgeYellow icon="RECEIPT_TEXT_OUTLINE">{content}</BadgeYellow>
          ),
        };

      case "requestPricing":
        return {
          type: "default",
          mainPaymentOption: "Request pricing to order",
          longText: "Request pricing to order",
          backgroundColor: "bg-zinc-50",
          textColor: null,
          borderColor: null,
          icon: "ARROW_TOP_RIGHT" as IconType,
          supplierCardIcon: "ARROW_TOP_RIGHT" as IconType,
          badge: (content: ReactNode) => (
            <BadgeZinc icon="ARROW_TOP_RIGHT">{content}</BadgeZinc>
          ),
        };

      case "sellarCredit":
        return {
          type: "sellarCredit",
          mainPaymentOption: "Sellar Credit",
          longText: "Order with Sellar Credit",
          backgroundColor: "bg-purple-100",
          textColor: "text-purple-700",
          borderColor: "border-purple-300",
          icon: "SELLAR_NEW" as IconType,
          supplierCardIcon: "LIGHTNING_BOLT" as IconType,
          badge: (content: ReactNode) => (
            <Badge
              icon="SELLAR_NEW"
              className="bg-gradient-to-br from-[#B557F1] to-[#D213A6] text-white"
            >
              {content}
            </Badge>
          ),
        };

      case "termsRequested":
        return {
          type: "termsRequested",
          mainPaymentOption: "Credit terms requested",
          longText: "Credit terms request pending",
          backgroundColor: "bg-lime-100",
          textColor: "text-lime-700",
          borderColor: "border-lime-300",
          icon: "TIMER_SAND_EMPTY" as IconType,
          supplierCardIcon: "TIMER_SAND_EMPTY" as IconType,
          badge: (content: ReactNode) => (
            <BadgeLime icon="TIMER_SAND_EMPTY">{content}</BadgeLime>
          ),
        };

      default:
        return {
          type: null,
          mainPaymentOption: null,
          longText: null,
          backgroundColor: null,
          textColor: null,
          borderColor: null,
          icon: null,
          supplierCardIcon: null,
          badge: null,
        };
    }
  };

  const indicatorOptions = {
    ...getIndicatorOptions(),
  };
  const hasOverrideText = overrideText !== undefined;
  const fullTextOnly = hasOverrideText
    ? overrideText
    : indicatorOptions.mainPaymentOption;
  const fullBadgeContent = overrideText === "" ? null : fullTextOnly;

  return {
    ...indicatorOptions,
    fullTextOnly,
    ...(!!indicatorOptions.supplierCardIcon
      ? {
          supplierCard: (
            <TooltipContainer enable={!!tooltipContent} text={tooltipContent}>
              <p
                className={twMerge(
                  "inline-flex gap-2",
                  indicatorOptions.textColor,
                )}
              >
                <Icon icon={indicatorOptions.supplierCardIcon} />{" "}
                {indicatorOptions.longText}
              </p>
            </TooltipContainer>
          ),
        }
      : {
          supplierCard: null,
        }),
    ...(!!indicatorOptions.icon
      ? {
          compact: (
            <TooltipContainer enable={!!tooltipContent} text={tooltipContent}>
              <p
                className={twMerge(
                  "inline-flex gap-2",
                  indicatorOptions.textColor,
                )}
              >
                <Icon icon={indicatorOptions.icon} />{" "}
                {indicatorOptions.mainPaymentOption}
              </p>
            </TooltipContainer>
          ),
        }
      : {
          compact: null,
        }),
    ...(!!indicatorOptions.badge
      ? {
          full: (
            <TooltipContainer enable={!!tooltipContent} text={tooltipContent}>
              {indicatorOptions.badge(fullBadgeContent)}
            </TooltipContainer>
          ),
        }
      : {
          full: null,
        }),
  };
};
